import Toolbar from '@material-ui/core/Toolbar';
import {Route, useLocation} from 'react-router-dom';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputBase,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import React, {useCallback, useEffect, useState} from 'react';
import {
  IDepot,
  IFarmer,
  IFarmInputInvoice,
  IFarmInputQuote,
  IFarmInputQuoteLineItem,
  IInputQuoteAddress,
  IInputQuoteProductInput,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  apiCreateQuote,
  apiGetDepotOrder,
  apiGetDepotQuote,
  apiGetDepots,
  apiGetInputProducts,
  apiGetNearestDepots,
  apiSearchFarmersAsSupplier,
} from 'constants/Api';
import GooglePlacesLocationSearch from 'components/GooglePlacesLocationSearch/GooglePlacesLocationSearch';
import {_ToTitleCase, GOOGLE_API_KEY} from 'constants/index';
import {QuoteProductInfo} from 'screens/Dashboard/Quotes/QuoteProducts/QuoteProducts';
import SearchIcon from '@material-ui/icons/Search';
import {NavigationService} from 'services/index';
import {ProductState} from 'appredux/reducers/reducer-products';
import moment from 'moment';
import {
  reduxAddQuote,
  reduxSetDepots,
  reduxSetProducts,
} from 'appredux/actions';
import AddIcon from '@material-ui/icons/Add';
import {CreateFarmerDialog} from 'components/Farmer/CreateFarmerDialog';
import {DepotState} from 'appredux/reducers/reducer-depots';
import Autocomplete from '@material-ui/lab/Autocomplete';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function QuoteCreate(props: {
  user: ISupplierUserProfile;
  productState: ProductState;
  depotState: DepotState;
}) {
  const [isLoadingAllProducts, setIsLoadingAllProducts] = useState(false);
  const [isLoadingAllDepots, setIsLoadingAllDepots] = useState(false);

  const query = useQuery();
  const [isLoadingQuote, setIsLoadingQuote] = useState<boolean>(false);
  const [quote, setQuote] = useState<IFarmInputQuote>();

  const [lineItemsToAdd, setLineItemsToAdd] = useState<
    IFarmInputQuoteLineItem[]
  >([]);
  const [lineItemsToUpdate, setLineItemsToUpdate] = useState<
    IFarmInputQuoteLineItem[]
  >([]);
  const [lineItemsToRemove, setLineItemsToRemove] = useState<number[]>([]);

  const [createFarmerDialogIsOpen, setCreateFarmerDialogIsOpen] =
    useState(false);
  const [farmerSearchTerm, setFarmerSearchTerm] = useState<string>('');
  const [isLoadingFarmers, setIsLoadingFarmers] = useState<boolean>(false);
  const [farmers, setFarmers] = useState<IFarmer[]>([]);
  const [selectedFarmer, setSelectedFarmer] = useState<IFarmer>();
  const [region, setRegion] = useState<Region>();
  const [isVatExempt, setIsVatExempt] = useState(false);
  const [depot, setDepot] = useState<IDepot | undefined>(props.user.depot);
  const [contactPersonName, setContactPersonName] = useState<string>();
  const [contactPersonPhone, setContactPersonPhone] = useState<string>();
  const [deliveryInstructions, setDeliveryInstructions] = useState<string>();
  const [logisticsCost, setLogisticsCost] = useState<number>();

  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const [isCreatingQuote, setIsCreatingQuote] = useState<boolean>(false);

  const loadAllProducts = useCallback(() => {
    const {user, productState} = props;
    const {lastUpdate, products} = productState;

    const productsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(8, 'hours'),
    );
    const noProducts = !products?.length;

    if (productsAreExpired || noProducts) {
      setIsLoadingAllProducts(true);
      apiGetInputProducts(user)
        .then((products) => reduxSetProducts(products))
        .catch((error) => {
          alert('Error loading products. Please try again later');
          console.error(error);
        })
        .finally(() => setIsLoadingAllProducts(false));
    }
  }, []);

  useEffect(loadAllProducts, [loadAllProducts]);

  const loadAllDepots = useCallback(() => {
    const {user, depotState} = props;
    const {lastUpdate, depots} = depotState;

    const depotsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(24, 'hours'),
    );
    const noDepots = !depots?.length;

    if (depotsAreExpired || noDepots) {
      setIsLoadingAllDepots(true);
      apiGetDepots(user)
        .then((depots) => reduxSetDepots(depots))
        .catch((error) => {
          alert('Error loading depots. Please try again later');
          console.error(error);
        })
        .finally(() => setIsLoadingAllDepots(false));
    }
  }, []);

  useEffect(loadAllDepots, [loadAllDepots]);

  const loadFarmers = useCallback(() => {
    setIsLoadingFarmers(true);
    apiSearchFarmersAsSupplier(farmerSearchTerm)
      .then(setFarmers)
      .catch((error) => {
        alert('Error loading farmers. Please try again later');
        console.error(error);
      })
      .then(() => setIsLoadingFarmers(false));
  }, [farmerSearchTerm]);

  useEffect(() => {
    if (farmerSearchTerm.length) {
      setIsLoadingFarmers(true);
      const timeout = setTimeout(loadFarmers, 1000);
      return () => clearTimeout(timeout);
    }
  }, [farmerSearchTerm, loadFarmers]);

  const loadQuote = useCallback((quoteId: number) => {
    setIsLoadingQuote(true);
    apiGetDepotQuote(quoteId)
      .then(setQuote)
      .catch((error) => {
        alert('Error loading quote. Please try again later');
        console.error(error);
      })
      .then(() => setIsLoadingQuote(false));
  }, []);

  useEffect(() => {
    const quoteId = query.get('quote');
    if (quoteId && !isNaN(Number(quoteId))) loadQuote(Number(quoteId));
  }, [query.get('quote'), loadQuote]);

  const loadInvoice = useCallback((invoiceId: number) => {
    setIsLoadingQuote(true);
    apiGetDepotOrder(invoiceId)
      .then(mapInvoiceToQuote)
      .then(setQuote)
      .catch((error) => {
        alert('Error loading invoice. Please try again later');
        console.error(error);
      })
      .then(() => setIsLoadingQuote(false));
  }, []);

  useEffect(() => {
    const invoiceId = query.get('invoice');
    if (invoiceId && !isNaN(Number(invoiceId))) loadInvoice(Number(invoiceId));
  }, [query.get('invoice'), loadInvoice]);

  const handleFarmerSelect = (farmer: IFarmer) => {
    setSelectedFarmer(farmer);
    setIsVatExempt(farmer.company?.vatExempt ?? false);
  };

  const handleFarmerDeselect = (farmer: IFarmer) => {
    setSelectedFarmer(undefined);
    setIsVatExempt(false);
  };

  const loadNearestDepot = useCallback(
    (region: Region) => {
      console.log(props.user);
      const getDepot =
        props.user.isAdmin ||
        props.user.isKhulaAdmin ||
        props.user.isCommodityGroup
          ? () => getNearestDepot(region)
          : () => Promise.resolve(props.user.depot!);
      getDepot().then((depot: IDepot) => {
        depot = depot || props.user.depot!;
        setDepot(depot);
        console.log('setting nearest depot: ', depot);
      });
    },
    [props.user],
  );

  useEffect(() => {
    if (!region) return;
    loadNearestDepot(region);
  }, [loadNearestDepot, region]);

  const handleQuoteCreate = () => {
    const lineItems: IInputQuoteProductInput[] = lineItemsToAdd
      .map(
        (line) =>
          lineItemsToUpdate.find((updateLine) => updateLine.id == line.id) ||
          line,
      )
      .filter((line) => !lineItemsToRemove.includes(line.id))
      .sort((a, b) => a.id - b.id)
      .map((line) => ({
        stockCode: line.product.stockCode,
        price: props.user.isCommodityGroup ? line.product.price : line.price,
        quantity: line.quantity,
        costPrice: props.user.isCommodityGroup ? undefined : line.costPrice,
      }));

    if (!region || !depot) return;
    setIsCreatingQuote(true);

    const deliveryAddress: IInputQuoteAddress = {
      address: region.address,
      latitude: region.latlong.split(',')[0].substring(0, 10),
      longitude: region.latlong.split(',')[1].substring(0, 10),
      deportId: depot.id,
    };
    return apiCreateQuote(
      selectedFarmer!.id,
      lineItems,
      deliveryAddress,
      isVatExempt,
      {
        contactPersonName: contactPersonName,
        contactPersonPhoneNumber: contactPersonPhone,
        deliveryInstructions: deliveryInstructions,
      },
      logisticsCost,
    )
      .then((response) => apiGetDepotQuote(response.quoteId))
      .then((quote) => reduxAddQuote(quote))
      .then(() => setShowSnackbar(true))
      .catch((err) => {
        alert('Failed to create quote at the moment. Please try again later.');
        console.error('Failed to create quote ', err);
      })
      .then(() => {
        setIsCreatingQuote(false);
        setTimeout(() => NavigationService.navigate('quotes'), 1000);
      });
  };

  const activeProducts = props.productState.products.filter(
    (product) => product.isActive,
  );

  return (
    <Box className="order-detail">
      <AppBar className="app-bar" elevation={0}>
        <Toolbar>
          <Route
            render={({history}) => (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => history.replace('/quotes')}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          />
          <Typography variant="h6" className="title">
            New Quote
          </Typography>
        </Toolbar>
      </AppBar>
      <div className="add-quote">
        <Grid container className="grid">
          <Grid item sm={4}>
            <Paper className="side-container" variant="outlined">
              <div className="customer-section">
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle1"
                    className="add-quote-subtitle"
                  >
                    Customer
                  </Typography>
                  <Button
                    variant="text"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateFarmerDialogIsOpen(true)}
                    size="small"
                    className="teams-btn"
                    color={'primary'}
                  >
                    New Customer
                  </Button>
                </Box>
                <CreateFarmerDialog
                  user={props.user}
                  open={createFarmerDialogIsOpen}
                  onClose={() => setCreateFarmerDialogIsOpen(false)}
                  onFarmerCreated={handleFarmerSelect}
                />
                {!selectedFarmer && (
                  <FarmerSearch
                    onSearchTermChange={setFarmerSearchTerm}
                    isLoading={isLoadingFarmers}
                  />
                )}
                {(!!selectedFarmer ||
                  (!!farmerSearchTerm.length && !isLoadingFarmers)) && (
                  <FarmerList
                    farmers={selectedFarmer ? [selectedFarmer] : farmers}
                    isSelected={!!selectedFarmer}
                    onFarmerSelect={handleFarmerSelect}
                    onFarmerDeselect={handleFarmerDeselect}
                  />
                )}
              </div>
              <div className="address-section">
                <Typography variant="subtitle1" className="add-quote-subtitle">
                  Address
                </Typography>
                <GooglePlacesLocationSearch
                  placeholder="Address"
                  queryCountries={['za']}
                  stylesInput={{height: 40, marginLeft: 0}}
                  googleApiKey={GOOGLE_API_KEY}
                  value={region?.address}
                  onSelect={setRegion}
                />
              </div>
              <div className="depot-section">
                <Typography variant="subtitle1" className="add-quote-subtitle">
                  Depot
                </Typography>
                <Autocomplete
                  disableClearable
                  options={props.depotState.depots}
                  getOptionLabel={(depot: IDepot) => _ToTitleCase(depot.name)}
                  getOptionSelected={(option) => option.id == depot?.id}
                  size="small"
                  value={depot}
                  onChange={(event, value) => setDepot(value)}
                  renderInput={(params) => (
                    <TextField {...params} size="small" variant="outlined" />
                  )}
                />
              </div>
              <div className="vat-section">
                <Typography variant="subtitle1" className="add-quote-subtitle">
                  VAT
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={!isVatExempt}
                      onChange={(event, checked) => setIsVatExempt(!checked)}
                    />
                  }
                  label={
                    <Typography variant={'body2'} color={'textSecondary'}>
                      Add VAT on quote PDF document?
                    </Typography>
                  }
                />
              </div>
              <div className="delivery-section">
                <Typography variant="subtitle1" className="add-quote-subtitle">
                  Delivery Details
                </Typography>
                <Box className="delivery-fields">
                  <TextField
                    className="input"
                    label="Contact Person Name"
                    variant="outlined"
                    fullWidth
                    type="text"
                    size="small"
                    value={contactPersonName}
                    onChange={(event) =>
                      setContactPersonName(event.target.value)
                    }
                  />
                  <TextField
                    className="input"
                    label="Contact Person Phone Number"
                    variant="outlined"
                    fullWidth
                    type="text"
                    size="small"
                    value={contactPersonPhone}
                    onChange={(event) =>
                      setContactPersonPhone(event.target.value)
                    }
                  />
                  <TextField
                    className="input"
                    label="Delivery Instructions"
                    variant="outlined"
                    fullWidth
                    type="text"
                    size="small"
                    multiline
                    value={deliveryInstructions}
                    onChange={(event) =>
                      setDeliveryInstructions(event.target.value)
                    }
                  />
                  <TextField
                    className="input"
                    label="Logistics Cost (R)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    size="small"
                    multiline
                    value={logisticsCost}
                    onChange={(event) =>
                      setLogisticsCost(
                        parseFloat(event.target.value) || undefined,
                      )
                    }
                  />
                </Box>
              </div>
              <div style={{flex: 1}} />
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="center"
                className="action-button-container"
              >
                <Button
                  color="primary"
                  variant="contained"
                  disableElevation
                  onClick={handleQuoteCreate}
                  disabled={
                    !selectedFarmer || !lineItemsToAdd.length || !region
                  }
                >
                  {isCreatingQuote ? (
                    <CircularProgress size={20} thickness={6} color="inherit" />
                  ) : (
                    'Create Quote'
                  )}
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid item sm={8}>
            <Paper className="line-items-container" variant="outlined">
              <Typography
                variant="subtitle1"
                className="add-quote-subtitle line-items-subtitle"
              >
                Line Items
              </Typography>
              <QuoteProductInfo
                isLoading={
                  isLoadingAllProducts || isLoadingAllDepots || isLoadingQuote
                }
                quote={{...(quote || emptyQuote), state: 'PENDING'}}
                allInputProducts={activeProducts}
                onLineItemAdd={(newLineItem) => {
                  setLineItemsToAdd((prevValues) => [
                    newLineItem,
                    ...prevValues,
                  ]);
                }}
                onLineItemUpdate={(updatedLineItem) => {
                  setLineItemsToUpdate((prevValues) => [
                    updatedLineItem,
                    ...prevValues,
                  ]);
                }}
                onLineItemRemove={(lineItemId) => {
                  setLineItemsToRemove((prevValues) => [
                    lineItemId,
                    ...prevValues,
                  ]);
                }}
                onSubmitQuote={handleQuoteCreate}
                isSubmittingQuote={isCreatingQuote}
                isCreating
                isCommodityGroup={props.user.isCommodityGroup}
              />
            </Paper>
          </Grid>
        </Grid>
      </div>
      <Snackbar
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        autoHideDuration={3000}
        message="Quote created"
      />
    </Box>
  );
}

export function FarmerSearch(props: {
  onSearchTermChange: (searchTerm: string) => any;
  isLoading?: boolean;
}) {
  return (
    <Paper
      component="form"
      className="farmer-search-container"
      variant="outlined"
    >
      <InputBase
        className="farmer-search-input"
        placeholder="Search farmers"
        onChange={(event) => props.onSearchTermChange(event.target.value)}
      />
      {props.isLoading ? (
        <CircularProgress size={20} thickness={6} className="search-icon" />
      ) : (
        <SearchIcon className="search-icon" />
      )}
    </Paper>
  );
}

export function FarmerList(props: {
  farmers: IFarmer[];
  isSelected?: boolean;
  onFarmerSelect: (farmer: IFarmer) => any;
  onFarmerDeselect: (farmer: IFarmer) => any;
}) {
  if (!props.farmers?.length)
    return (
      <Card variant="outlined" className="farmer-list">
        <Box p={1}>
          <Typography variant="caption">No search results</Typography>
        </Box>
      </Card>
    );
  return (
    <Card variant="outlined" className="farmer-list">
      <List dense>
        {props.farmers.map((farmer) => (
          <ListItem
            button
            key={farmer.id}
            onClick={() => props.onFarmerSelect(farmer)}
          >
            <ListItemText
              primary={farmer.name + ' ' + farmer.surname}
              secondary={farmer.address}
              // secondary={farmer.farm?.name}
            />
            <ListItemSecondaryAction>
              <Checkbox
                edge="end"
                onChange={() =>
                  props.isSelected
                    ? props.onFarmerDeselect(farmer)
                    : props.onFarmerSelect(farmer)
                }
                checked={props.isSelected}
                color="primary"
              />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Card>
  );
}

const mapInvoiceToQuote = (invoice: IFarmInputInvoice) => {
  const quote: IFarmInputQuote = {
    ...emptyQuote,
    total: invoice.total,
    lineItems: invoice.orders.map((order) => ({
      ...order,
      retailPrice: order.product.price,
      originalQuantity: Number(order.quantity),
      quantity: Number(order.quantity),
    })),
  };
  return quote;
};

const emptyQuote: IFarmInputQuote = {
  id: Math.random(),
  quoteNumber: Math.random().toString(8),
  insertedDate: new Date().toString(),
  updateDate: new Date().toString(),
  latitude: '',
  longitude: '',
  address: '',
  total: 0,
  farmer: {} as IFarmer,
  lineItems: [],
  depot: {} as IDepot,
  isForDelivery: false,
  deliveryType: 'Collection',
  state: 'PENDING',
  formattedState: 'Pending',
  isExpired: false,
};

type Region = {
  address: string;
  latlong: string;
  country: string;
  province: string;
};

async function getNearestDepot(region: Region) {
  console.log('region: ', region);

  const nearestDepots: IDepot[] = await apiGetNearestDepots(
    region.latlong.split(',')[0],
    region.latlong.split(',')[1],
  );
  return nearestDepots[0];
}
